import "./styles.css";
import { SPECIMENS, SAMPLE_QUERIES, scoreSpecimen, type Specimen } from "./demo-data";

const $ = <T extends HTMLElement>(sel: string): T => {
  const el = document.querySelector<T>(sel);
  if (!el) throw new Error(`missing element: ${sel}`);
  return el;
};

/* ---------------- specimen drawer ---------------- */

const dialog = $<HTMLDialogElement>("#specimen-dialog");
const dialogBody = $<HTMLDivElement>("#dialog-body");

function openSpecimen(s: Specimen): void {
  dialogBody.innerHTML = `
    <img class="dlg-img" src="${s.image}" alt="${s.alt}" />
    <div class="dlg-meta">
      <span class="dlg-no">${s.catalogNo} · ${s.kind}</span>
      <h3 class="dlg-title" id="dialog-title">${s.title}</h3>
      <p class="dlg-extract">“${s.extracted}”</p>
      <div class="dlg-fields">
        ${s.fields.map((f) => `<div><b>${f.label}</b><span>${f.value}</span></div>`).join("")}
      </div>
      <p class="dlg-src">${originLabel(s)} · saved ${s.savedAt} · sample data</p>
    </div>`;
  dialog.showModal();
}

function renderDrawer(): void {
  const grid = $<HTMLUListElement>("#drawer-grid");
  for (const s of SPECIMENS) {
    const li = document.createElement("li");
    const btn = document.createElement("button");
    btn.className = "spec-card";
    btn.setAttribute("aria-label", `Open catalogue sheet: ${s.title}`);
    btn.innerHTML = `
      <figure>
        <img src="${s.image}" alt="${s.alt}" loading="lazy" />
        <span class="spec-no">${s.catalogNo}</span>
      </figure>
      <div class="spec-meta">
        <span class="spec-kind">${s.kind}</span>
        <h3 class="spec-title">${s.title}</h3>
        <span class="spec-src">${originLabel(s)} · ${s.savedAt}</span>
      </div>`;
    btn.addEventListener("click", () => openSpecimen(s));
    li.appendChild(btn);
    grid.appendChild(li);
  }
}

$("#dialog-close").addEventListener("click", () => dialog.close());
dialog.addEventListener("click", (e) => {
  if (e.target === dialog) dialog.close();
});

/* ---------------- reading-room demo ---------------- */

const input = $<HTMLInputElement>("#demo-input");
const chips = $<HTMLDivElement>("#demo-chips");
const empty = $<HTMLDivElement>("#demo-empty");
const resultsEl = $<HTMLDivElement>("#demo-results");
const actionsEl = $<HTMLDivElement>("#demo-actions");
const actionCard = $<HTMLDivElement>("#action-card");

for (const q of SAMPLE_QUERIES) {
  const chip = document.createElement("button");
  chip.type = "button";
  chip.className = "chip";
  chip.textContent = q;
  chip.addEventListener("click", () => {
    input.value = q;
    runQuery(q);
  });
  chips.appendChild(chip);
}

function confidenceBadge(c: number): string {
  const pct = Math.round(c * 100);
  const cls = c >= 0.9 ? "conf-high" : "conf-mid";
  const word = c >= 0.9 ? "high" : "medium";
  return `<span class="conf-badge ${cls}">${word} confidence · ${pct}%</span>`;
}

function originLabel(s: Specimen): string {
  switch (s.origin) {
    case "user":
      return `origin — user-tagged: ${s.sourceApp}`;
    case "inferred":
      return `origin — inferred: ${s.sourceApp} (unverified)`;
    default:
      return "origin — unknown";
  }
}

function resultCard(s: Specimen, i: number): string {
  return `
    <button class="result-card" data-id="${s.id}" style="animation-delay:${i * 70}ms"
        aria-label="Result: ${s.title} — open catalogue sheet">
      <img src="${s.image}" alt="" />
      <div class="result-main">
        <h4>${s.title}</h4>
        <div class="result-meta">
          <span>${s.catalogNo}</span><span>·</span><span>${s.kind}</span><span>·</span>${confidenceBadge(s.confidence)}
        </div>
        <p class="result-extract">${s.extracted}</p>
        <div class="result-fields">
          ${s.fields.slice(0, 3).map((f) => `<div><b>${f.label}</b>${f.value}</div>`).join("")}
        </div>
        <p class="provenance">
          <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true"><rect x="4" y="3" width="16" height="18" rx="2" fill="none" stroke="currentColor" stroke-width="2"/><line x1="8" y1="8" x2="16" y2="8" stroke="currentColor" stroke-width="2"/><line x1="8" y1="12" x2="14" y2="12" stroke="currentColor" stroke-width="2"/></svg>
          ${originLabel(s)} · saved ${s.savedAt}
        </p>
      </div>
    </button>`;
}

let approvedIds = new Set<string>();

function renderAction(s: Specimen): void {
  if (!s.action || approvedIds.has(s.id)) {
    actionsEl.hidden = true;
    actionCard.innerHTML = "";
    return;
  }
  const a = s.action;
  actionCard.innerHTML = `
    <div>
      <span class="ac-kind">${a.kind === "calendar" ? "Calendar suggestion" : "Reminder suggestion"}</span>
      <strong>${a.title}</strong>
      <span class="ac-detail">${a.detail}</span>
      <span class="ac-when">${a.when}</span>
    </div>
    <div class="ac-buttons">
      <button class="ac-approve" type="button">Approve</button>
      <button class="ac-dismiss" type="button">Dismiss</button>
    </div>`;
  actionsEl.hidden = false;

  actionCard.querySelector<HTMLButtonElement>(".ac-approve")!.addEventListener("click", () => {
    approvedIds.add(s.id);
    actionCard.innerHTML = `
      <span class="ac-done">
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><circle cx="12" cy="12" r="10" fill="none" stroke="currentColor" stroke-width="2"/><path d="M8 12.5l2.5 2.5L16 9.5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
        ${a.kind === "calendar" ? "Calendar entry" : "Reminder"} scheduled — this is a demo, nothing was written to your device.
      </span>`;
  });
  actionCard.querySelector<HTMLButtonElement>(".ac-dismiss")!.addEventListener("click", () => {
    actionsEl.hidden = true;
    actionCard.innerHTML = "";
  });
}

function runQuery(query: string): void {
  const ranked = SPECIMENS.map((s) => ({ s, score: scoreSpecimen(query, s) }))
    .filter((r) => r.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 2);

  if (ranked.length === 0) {
    resultsEl.innerHTML = `<p class="demo-empty" style="padding:1.6rem">Nothing filed under “${escapeHtml(query)}” in the sample drawer. Try one of the sample questions.</p>`;
    resultsEl.hidden = false;
    empty.hidden = true;
    actionsEl.hidden = true;
    return;
  }

  resultsEl.innerHTML = ranked.map((r, i) => resultCard(r.s, i)).join("");
  resultsEl.hidden = false;
  empty.hidden = true;

  resultsEl.querySelectorAll<HTMLButtonElement>(".result-card").forEach((el) => {
    el.addEventListener("click", () => {
      const s = SPECIMENS.find((x) => x.id === el.dataset.id);
      if (s) openSpecimen(s);
    });
  });

  renderAction(ranked[0].s);
}

function escapeHtml(t: string): string {
  return t.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}

$("#demo-form").addEventListener("submit", (e) => {
  e.preventDefault();
  const q = input.value.trim();
  if (q) runQuery(q);
});

renderDrawer();

/* ---------------- scroll reveal ---------------- */

const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
if (!prefersReduced && "IntersectionObserver" in window) {
  const io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (e.isIntersecting) {
          e.target.classList.add("is-in");
          io.unobserve(e.target);
        }
      }
    },
    { threshold: 0.12 },
  );
  document
    .querySelectorAll(".section-head, .steps li, .promise-grid article, .drawer-grid li, .pilot-card, .device-wrap")
    .forEach((el) => {
      el.classList.add("reveal");
      io.observe(el);
    });
}
